import { useSuspenseQuery } from '@apollo/client'
import { FetchConnectionsWithStreamsDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath, HandleQuery } from '@salescore/client-common'
// eslint-disable-next-line sonarjs/deprecation
import type { useModal } from '@salescore/frontend-common'
import { Button, Col, Form, Modal, Row, Select } from 'antd'
import { Array, Order, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactNode, useState } from 'react'

const Body = ({ onSave }: { onSave: (items: Array<{ value: string; label: string }>) => void }): ReactNode => {
  const {
    data: { connections },
  } = useSuspenseQuery(FetchConnectionsWithStreamsDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
    fetchPolicy: 'cache-and-network',
  })
  const [streamName, setStreamName] = useState<string>()
  const [propertyName, setPropertyName] = useState<string>()

  const streams = connections.filter((x) => x.source.provider !== 'salescore').flatMap((x) => x.streams)
  const pickedStream = streams.find((x) => x.name === streamName)
  const properties = pickedStream?.properties ?? []
  const pickedProperty = properties.find((x) => x.name === propertyName)
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: SQLを実行して選択肢以外も取得
  const propertyOptionGroups = pipe(
    properties,
    Array.filter((x) => (x.meta.selectOptions ?? []).length > 0),
    Array.map((property) => ({
      label: property.meta.label,
      value: property.name,
      type: property.destination.type,
      key: property.meta.label,
    })),
    Array.groupBy((x): string => x.type),
    Record.toEntries,
  )
  const pickedSelectOptions = pickedProperty?.meta.selectOptions ?? []
  const dimensions = pickedSelectOptions.map((x) => ({ value: x.value, label: x.label })).filter(isNotNullish)

  return (
    <div>
      <Row justify="space-between">
        <Col span={11}>
          <Form>
            {/* PropertyCascaderを使うとUIを統一できて便利だが、全てのstreamの全てのpropertyを計算するとそれなりに重くなりそうなので一応避ける */}
            <Row justify="space-between">
              <Col span={20}>
                <Form.Item label={t(`オブジェクト名`)}>
                  <Select
                    onChange={(event) => {
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      setStreamName(event as string)
                      setPropertyName(undefined)
                    }}
                    options={streams.map((stream) => ({
                      label: stream.meta.label,
                      key: stream.meta.label,
                      value: stream.name,
                    }))}
                    showSearch
                    filterOption={(input, option) => (option?.key ?? '').toLowerCase().includes(input.toLowerCase())}
                  />
                </Form.Item>
              </Col>
              <Col span={20}>
                <Form.Item label={t(`項目名`)}>
                  <Select
                    onChange={(event) => {
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      setPropertyName(event as string)
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-unnecessary-condition
                      ((option?.key as string) ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    disabled={pickedStream === undefined}
                  >
                    {propertyOptionGroups.map(([groupName, options], index) => (
                      // 手抜き
                      <Select.OptGroup key={index} label={<>{t(`文字列`)}</>}>
                        {pipe(
                          options,
                          Array.sortBy(Order.mapInput(Order.Number, (x) => x.label.length)),
                          Array.map((option) => (
                            <Select.Option value={option.value} key={[option.value, option.label].join('-')}>
                              {option.label}
                            </Select.Option>
                          )),
                        )}
                      </Select.OptGroup>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Row justify="end" className="w-full">
                <Col>
                  <Button
                    type={'primary'}
                    onClick={() => {
                      onSave(dimensions)
                    }}
                  >
                    {t(`インポート`)}
                  </Button>
                </Col>
              </Row>
            </Row>
          </Form>
        </Col>
        <Col span={12}>
          <div>
            {pickedSelectOptions.length > 0 ? (
              <div>
                <div>{t(`インポートされる項目`)}</div>
                <pre className="bg-gray-100 p-4 text-xs">
                  {dimensions.map((option) => `${option.label}, ${option.value}`).join('\n')}
                </pre>
              </div>
            ) : (
              <div></div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  )
}

export const ImportDimensionModal = ({
  onSave,
  modal,
}: {
  onSave: (items: Array<{ value: string; label: string }>) => void
  // eslint-disable-next-line sonarjs/deprecation
  modal: ReturnType<typeof useModal>
}): ReactNode => (
  <Modal
    open={modal.isModalVisible}
    onCancel={modal.hideModal}
    width={'60%'}
    cancelText={t(`閉じる`)}
    okButtonProps={{ style: { display: 'none' } }}
    title={<div>{t(`目標軸のインポート`)}</div>}
    style={{ top: '10%' }}
  >
    <HandleQuery>
      <Body onSave={onSave} />
    </HandleQuery>
  </Modal>
)
